import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, timeout as rxTimeout } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface AgentResponse {
  success: boolean;
  message: string;
  sessionId: string;
}

@Injectable({ providedIn: 'root' })
export class AiApiService {
  private baseUrl = environment.aiApiUrl;
  // /query and /agents/explain are served by openai-proxy, not the main backend.
  private aiProxyUrl = (environment as { aiProxyUrl?: string }).aiProxyUrl || '/ai-proxy';
  private agentSessionId: string | null = null;
  private readonly uploadTimeoutMs = 90000;

  constructor(private http: HttpClient) { }

  query(prompt: string): Observable<string> {
    const body = {
      prompt,
      model_type: 'azure',
      model_name: 'gpt-4'
    };

    return this.http.post(`${this.aiProxyUrl}/query`, body, {
      headers: { 'Content-Type': 'application/json' },
      responseType: 'text'
    });
  }

  /**
   * Query the Azure AI Agent with streaming support.
   * This calls the /api/agent/chat endpoint which uses the Azure AI Agent SDK.
   * @param message The user's message
   * @param sessionId Optional session ID for conversation continuity
   * @returns Observable that emits streamed text chunks
   */
  queryAgent(message: string, sessionId?: string): Observable<string> {
    const subject = new Subject<string>();

    // Use provided sessionId or the stored one
    const effectiveSessionId = sessionId || this.agentSessionId;

    // Use streaming SSE endpoint
    const url = '/api/agent/chat';
    const body = {
      message,
      sessionId: effectiveSessionId,
      stream: true
    };

    // Create EventSource-like behavior using fetch
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    }).then(async (response) => {
      if (!response.ok) {
        const errorText = await response.text();
        subject.error(new Error(`Agent API error: ${errorText}`));
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        subject.error(new Error('No response body'));
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';
      let fullResponse = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process SSE lines
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') {
              subject.complete();
              return;
            }
            try {
              const parsed = JSON.parse(data);
              if (parsed.text) {
                fullResponse += parsed.text;
                subject.next(parsed.text);
              }
              if (parsed.sessionId) {
                this.agentSessionId = parsed.sessionId;
              }
              if (parsed.error) {
                subject.error(new Error(parsed.error));
                return;
              }
            } catch (e) {
              // Ignore parse errors for incomplete chunks
            }
          }
        }
      }

      subject.complete();
    }).catch((error) => {
      subject.error(error);
    });

    return subject.asObservable();
  }

  /**
   * Query the Azure AI Agent without streaming.
   * @param message The user's message
   * @param sessionId Optional session ID for conversation continuity
   * @returns Observable with the full response
   */
  queryAgentSync(message: string, sessionId?: string): Observable<AgentResponse> {
    const effectiveSessionId = sessionId || this.agentSessionId;

    return this.http.post<AgentResponse>('/api/agent/chat', {
      message,
      sessionId: effectiveSessionId,
      stream: false
    });
  }

  /**
   * Reset the agent session to start a new conversation
   */
  resetAgentSession(): void {
    this.agentSessionId = null;
  }

  /**
   * Get the current agent session ID
   */
  getAgentSessionId(): string | null {
    return this.agentSessionId;
  }

  uploadDocument(formData: FormData, options?: { mode?: string; compact?: boolean }): Observable<string> {
    const params = new URLSearchParams();
    if (options?.mode) params.set('mode', options.mode);
    const shouldCompact = options?.compact ?? options?.mode === 'receipt';
    if (shouldCompact) params.set('compact', '1');
    const query = params.toString();
    const suffix = query ? '?' + query : '';

    const primaryUrl = this.baseUrl + '/upload' + suffix;   // customer-onboarding path
    const fallbackApiUrl = '/api/upload' + suffix;          // backend route alternative

    return this.http.post(primaryUrl, formData, { responseType: 'text' }).pipe(
      rxTimeout(this.uploadTimeoutMs),
      catchError((err: any) => {
        const status = Number(err?.status || 0);
        const isReceipt = options?.mode === 'receipt';
        if (isReceipt) return throwError(() => err);

        const canFallback = status === 404 || status === 405 || status === 0 || status === 502 || status === 503 || status === 504;
        if (!canFallback) return throwError(() => err);
        return this.http.post(fallbackApiUrl, formData, { responseType: 'text' }).pipe(
          rxTimeout(this.uploadTimeoutMs)
        );
      })
    );
  }

  explain(body: {
    question: string,
    currentKey?: string | null,
    pendingKeys: string[],
    registry: any
  }) {
    return this.http.post(`${this.aiProxyUrl}/agents/explain`, body);
  }
}
